import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useModal } from '../contexts/ModalContext';
import { Environment, Organization, Definition, DataBuild } from '../types';
import networkManager from '../services/NetworkManager';

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { openModal, closeModal } = useModal();
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadingCredentials, setUploadingCredentials] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // States for Organization Configs
  const [orgConfigFormData, setOrgConfigFormData] = useState({
    BuildJSON: false
  });
  const [buildJSONChecked, setBuildJSONChecked] = useState(false);
  const [uploadingOrgCredentials, setUploadingOrgCredentials] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const orgFileInputRef = useRef<HTMLInputElement>(null);
  const buildJSONRef = useRef<boolean>(false);
  
  // States for published content details
  const [publishedDefinitions, setPublishedDefinitions] = useState<Record<string, Definition>>({});
  const [publishedGamedata, setPublishedGamedata] = useState<Record<string, DataBuild>>({});
  const [loadingPublishedContent, setLoadingPublishedContent] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Effect to update modal when organization credentials change
  useEffect(() => {
    if (isModalOpen) {
      // Close and re-open modal to show updated state
      closeModal('org-config');
      setTimeout(() => {
        openOrgConfigModal();
      }, 50);
    }
  }, [organization?.hasCredentials]);

  // Effect to initialize form data when organization data is loaded (only when organization changes)
  useEffect(() => {
    if (organization) {
      const buildJSON = organization.config?.buildFormats?.JSON ?? false;
      buildJSONRef.current = buildJSON;
      setBuildJSONChecked(buildJSON);
      setOrgConfigFormData({
        BuildJSON: buildJSON
      });
    }
  }, [organization]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [environmentsData, organizationData] = await Promise.all([
        networkManager.getEnvironments(),
        networkManager.getOrganizationInfo()
      ]);
      setEnvironments(environmentsData);
      setOrganization(organizationData);
      setError(null);
      
      // Load published content details
      await loadPublishedContentDetails(environmentsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const loadPublishedContentDetails = async (environmentsData: Environment[]) => {
    try {
      setLoadingPublishedContent(true);
      
      // Collect unique definition and gamedata IDs
      const definitionIds = new Set<string>();
      const gamedataIds = new Set<string>();
      
      environmentsData.forEach(env => {
        if (env.publishedDefinitionID) {
          definitionIds.add(env.publishedDefinitionID);
        }
        if (env.publishedDataID) {
          gamedataIds.add(env.publishedDataID);
        }
      });
      
      // Only proceed if we have IDs to load
      if (definitionIds.size === 0 && gamedataIds.size === 0) {
        setLoadingPublishedContent(false);
        return;
      }
      
      // Load definitions and gamedata in parallel with better error handling
      const [definitions, gamedata] = await Promise.all([
        definitionIds.size > 0 ? Promise.all(
          Array.from(definitionIds).map(async (id) => {
            try {
              const definition = await networkManager.getDefinition(id);
              return { id, definition };
            } catch (err) {
              console.warn(`Failed to load definition ${id}:`, err);
              return { id, definition: null };
            }
          })
        ) : Promise.resolve([]),
        gamedataIds.size > 0 ? Promise.all(
          Array.from(gamedataIds).map(async (id) => {
            try {
              const gamedata = await networkManager.getDataBuild(id);
              return { id, gamedata };
            } catch (err) {
              console.warn(`Failed to load gamedata ${id}:`, err);
              return { id, gamedata: null };
            }
          })
        ) : Promise.resolve([])
      ]);
      
      // Create lookup objects
      const definitionsMap: Record<string, Definition> = {};
      const gamedataMap: Record<string, DataBuild> = {};
      
      definitions.forEach(({ id, definition }) => {
        if (definition) {
          definitionsMap[id] = definition;
        }
      });
      
      gamedata.forEach(({ id, gamedata }) => {
        if (gamedata) {
          gamedataMap[id] = gamedata;
        }
      });
      
      setPublishedDefinitions(definitionsMap);
      setPublishedGamedata(gamedataMap);
      
    } catch (err) {
      console.error('Error loading published content details:', err);
      // Don't show error to user as this is not critical functionality
    } finally {
      setLoadingPublishedContent(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleCredentialsUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingCredentials(true);
      setError(null);
      
      await networkManager.uploadCredentials(file);
      
      // Refresh organization info to get updated credentials status
      const updatedOrg = await networkManager.getOrganizationInfo();
      setOrganization(updatedOrg);
      
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Show success notification
      showNotification('Credentials uploaded successfully!', 'success', 5000);
      
    } catch (err) {
      // Show error notification
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload credentials';
      showNotification(errorMessage, 'error', 8000);
    } finally {
      setUploadingCredentials(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Organization Configs Modal Handlers
  const handleOrgConfigInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setOrgConfigFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : e.target.value
    }));
  };

  const handleBuildJSONChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    buildJSONRef.current = checked;
  };

  const handleOrgCredentialsUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingOrgCredentials(true);
      setError(null);
      
      await networkManager.uploadCredentials(file);
      
      // Refresh organization info to get updated credentials status
      const updatedOrg = await networkManager.getOrganizationInfo();
      setOrganization(updatedOrg);
      
      // Clear the file input
      if (orgFileInputRef.current) {
        orgFileInputRef.current.value = '';
      }
      
      // Show success notification
      showNotification('Credentials uploaded successfully!', 'success', 5000);
      
    } catch (err) {
      // Show error notification
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload credentials';
      showNotification(errorMessage, 'error', 8000);
    } finally {
      setUploadingOrgCredentials(false);
    }
  };

  const handleOrgUploadClick = () => {
    orgFileInputRef.current?.click();
  };

  const handleOrgConfigSubmit = async () => {
    try {
      await networkManager.updateOrganizationConfig({
        BuildJSON: buildJSONRef.current
      });
      
      // Refresh organization info to get updated config
      const updatedOrg = await networkManager.getOrganizationInfo();
      setOrganization(updatedOrg);
      
      showNotification('Organization configuration saved successfully!', 'success', 5000);
      setIsModalOpen(false);
      closeModal('org-config');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save configuration';
      showNotification(errorMessage, 'error', 8000);
    }
  };

  // Component for modal content that re-renders when state changes
  const OrgConfigModalContent = () => (
    <div className="space-y-6">
      {/* Credentials Upload Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Credentials Status
        </label>
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-gray-700">Current Status</p>
              <p className={`text-xs ${
                organization?.hasCredentials ? 'text-green-600' : 'text-red-600'
              }`}>
                {organization?.hasCredentials ? 'Credentials configured' : 'No credentials uploaded'}
              </p>
            </div>
          </div>
          <button
            onClick={handleOrgUploadClick}
            disabled={uploadingOrgCredentials}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {uploadingOrgCredentials ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </>
            ) : (
              <>
                <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {organization?.hasCredentials ? 'Update' : 'Upload'}
              </>
            )}
          </button>
        </div>
        
        {/* Hidden file input for organization credentials */}
        <input
          ref={orgFileInputRef}
          type="file"
          accept=".json"
          onChange={handleOrgCredentialsUpload}
          className="hidden"
        />
      </div>

      {/* Build Formats Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Build Formats
        </label>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="BuildJSON"
                name="BuildJSON"
                type="checkbox"
                defaultChecked={buildJSONRef.current}
                onChange={handleBuildJSONChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="BuildJSON" className="font-medium text-gray-700">
                Generate JSON Files
              </label>
              <p className="text-gray-500 mt-1">
                Enable this option to generate JSON files in addition to binary files during the build process.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Actions */}
      <div className="flex justify-end space-x-3 pt-4">
        <button
          onClick={() => {
            setIsModalOpen(false);
            closeModal('org-config');
          }}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleOrgConfigSubmit}
          className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        >
          Save Configuration
        </button>
      </div>
    </div>
  );

  const openOrgConfigModal = () => {
    setIsModalOpen(true);
    openModal({
      id: 'org-config',
      title: 'Organization Configuration',
      content: <OrgConfigModalContent />,
      size: 'md'
    });
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.name || 'User'}!
          </h1>
          <p className="text-gray-600 mt-1">
            Here's what's happening with your Basilisk account
          </p>
        </div>
        <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
          <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
      </div>

      {/* User and Organization Information */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
              <svg className="h-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Account Information</h2>
          </div>
          <button
            onClick={openOrgConfigModal}
            className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Configure organization settings"
          >
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* User Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">User Profile</h3>
            
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="h-4 w-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Name</p>
                <p className="text-lg font-semibold text-gray-900">{user?.name}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Email</p>
                <p className="text-lg font-semibold text-gray-900">{user?.email}</p>
              </div>
            </div>
            

          </div>

          {/* Organization Information */}
          {organization && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Organization</h3>
              
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center">
                  <svg className="h-4 w-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Organization Name</p>
                  <p className="text-lg font-semibold text-gray-900">{organization.organizationName}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">Credentials Status</p>
                  {organization.hasCredentials ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Configured
                    </span>
                  ) : (
                    <button
                      onClick={openOrgConfigModal}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200 transition-colors cursor-pointer"
                      title="Click to configure credentials"
                    >
                      Not Configured
                    </button>
                  )}
                </div>
              </div>
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleCredentialsUpload}
                className="hidden"
              />
            </div>
          )}
        </div>
      </div>

      {/* Environments Preview */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Recent Environments
          </h2>
          <Link
            to="/environments"
            className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
          >
            View All
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600">{error}</p>
          </div>
        ) : environments.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Environments</h3>
            <p className="text-gray-500 mb-4">You haven't created any environments yet.</p>
            <Link
              to="/environments"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Create Environment
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Published Definition
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Published Gamedata
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {environments.slice(0, 5).map((environment) => {
                  const publishedDefinition = environment.publishedDefinitionID 
                    ? publishedDefinitions[environment.publishedDefinitionID] 
                    : null;
                  const publishedGamedataItem = environment.publishedDataID 
                    ? publishedGamedata[environment.publishedDataID] 
                    : null;
                  
                  return (
                    <tr key={environment._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <Link
                          to={`/environments/${environment._id}`}
                          className="text-blue-600 hover:text-blue-900 hover:underline"
                        >
                          {environment.name}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {environment.description || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {loadingPublishedContent ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600 mr-2"></div>
                            Loading...
                          </div>
                        ) : publishedDefinition ? (
                          <div className="flex flex-col">
                            <Link
                              to={`/definitions/${environment.publishedDefinitionID}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                              target="_blank"
                            >
                              {publishedDefinition.title}
                            </Link>
                            <span className="text-xs text-gray-400">
                              by {environment.definitionPublisher?.name || 'Unknown'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">No definition published</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {loadingPublishedContent ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600 mr-2"></div>
                            Loading...
                          </div>
                        ) : publishedGamedataItem ? (
                          <div className="flex flex-col">
                            <Link
                              to={`/data-builds/${environment.publishedDataID}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                              target="_blank"
                            >
                              {publishedGamedataItem.title}
                            </Link>
                            <span className="text-xs text-gray-400">
                              by {environment.dataPublisher?.name || 'Unknown'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">No gamedata published</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(environment.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          to={`/environments/${environment._id}`}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default HomePage;
